import React, { useEffect, useCallback, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import * as services from "../services/saleng";
import { formatNumber } from "../utils/formatNumber";

const PriceSelectionDialog = ({
  open,
  onClose,
  onSelect,
  transaction,
  categoryId,
  productId,
  products = [],
  pricingMode = "category",
  currentWeight = 0,
  currentPrice,
  productName = "",
}) => {
  const [prices, setPrices] = useState([]);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPriceId, setSelectedPriceId] = useState("");
  const isActiveRef = useRef(false);
  const lastFetchKeyRef = useRef("");

  const fetchCategoriesAndCompose = useCallback(async () => {
    if (!open || !productId) return;

    isActiveRef.current = true;
    setIsLoading(true);

    try {
      const pid = Number(productId);

      const txId = transaction?.id;
      // โหลดคู่ขนาน: Best Price + Categories
      const [bestRes, catRes] = await Promise.all([
        txId ? services.getBestPrice(txId) : Promise.resolve(null),
        services.getCategories(),
      ]);

      const bestPayload = bestRes;
      const best = bestPayload?.bestHistory ?? {};
      const perItem = Array.isArray(best?.perItem) ? best.perItem : [];

      // หา entry ใน Best Price ของสินค้านี้ (ถ้าไม่มี ใช้ fallback)
      const entry = perItem.find((it) => Number(it.productId) === pid) || null;

      // สกัดค่าจาก best entry (ถ้ามี)
      const product = products.find((item) => Number(item?.id) === pid) || null;
      const basePrice = Number(entry?.basePrice ?? product?.basePrice ?? 0);
      const branchAdj = Number(
        entry?.branchAdjustment ?? product?.branchAdjustment ?? 0
      );
      const groupAdj = Number(
        entry?.groupAdjustment ?? product?.groupAdjustment ?? 0
      );
      const groupName =
        bestPayload?.group?.name ??
        entry?.groupName ??
        transaction?.customer?.customerGroup?.name ??
        transaction?.customerGroup?.name ??
        null;
      const hasCustomerGroup = Boolean(
        transaction?.customerGroupId ?? transaction?.customer?.customerGroupId
      );

      // base (รวมสาขา)
      const base = basePrice + branchAdj;

      // categories + match หมวด
      const categoryData = catRes?.data?.data || [];
      const matchedCategory =
        categoryData.find((cat) => Number(cat.id) === Number(categoryId)) ||
        null;

      // เตรียมราคาหมวด: base + adjPrice (ของ tier)
      if (matchedCategory) {
        const tiers = Array.isArray(matchedCategory.prices)
          ? matchedCategory.prices
          : [];
        matchedCategory.prices = tiers.map((price) => ({
          ...price,
          finalPrice: base + Number(price?.adjPrice ?? 0),
          minWeight: Number(price?.minWeight ?? 0),
        }));
        matchedCategory.finalPrice = base; // เผื่ออยากโชว์ base ของหมวด
      }

      if (!isActiveRef.current) return;
      setData(matchedCategory);

      const tierOptions = (matchedCategory?.prices ?? [])
        .map((price, index) => {
          const adjustment = Number(price?.adjPrice ?? 0);
          return {
            id: `tier-${price?.id ?? index}`,
            minWeight: Number(price?.minWeight ?? 0),
            finalPrice: Number(price?.finalPrice ?? base),
            label: `ราคาขั้นบันได ขั้นต่ำ ${formatNumber(price?.minWeight ?? 0)} กก.`,
            detail:
              adjustment === 0
                ? "เท่าราคาพื้นฐาน"
                : `ปรับจากราคาพื้นฐาน ${adjustment > 0 ? "+" : ""}${formatNumber(adjustment)} บาท/กก.`,
            kind: "buy-tier",
          };
        })
        .sort((a, b) => a.minWeight - b.minWeight);

      const applicableTier = [...tierOptions]
        .sort((a, b) => a.minWeight - b.minWeight)
        .reduce(
          (selected, tier) =>
            tier.minWeight <= Number(currentWeight) ? tier : selected,
          null
        );

      const combined =
        pricingMode === "buy"
          ? [
              {
                id: "base",
                finalPrice: base,
                label: "ราคาพื้นฐานของระบบตามสาขา",
                detail: "ราคาประกาศของระบบ + ส่วนปรับราคาสาขา",
                kind: "base",
              },
              ...tierOptions,
              {
                id: "group",
                finalPrice: hasCustomerGroup ? base + groupAdj : base,
                label: groupName
                  ? `ราคาตามกลุ่มลูกค้า (${groupName})`
                  : "ราคาตามกลุ่มลูกค้า (ไม่มีกลุ่ม)",
                detail: hasCustomerGroup
                  ? "ราคาพื้นฐาน + ราคากลุ่ม"
                  : "ใช้ราคาพื้นฐาน",
                kind: "group",
              },
            ]
          : [
              {
                id: "base",
                minWeight: 0,
                finalPrice: base,
                label: "ราคาพื้นฐาน",
                kind: "base",
              },
              ...(matchedCategory?.prices ?? []).map((p) => ({
                id: p.id,
                minWeight: Number(p.minWeight ?? 0),
                finalPrice: Number(p.finalPrice ?? 0),
                label: `หมวด ${matchedCategory?.name ?? ""}`,
                kind: "category",
              })),
            ]
              .filter(Boolean)
              .sort((a, b) => (a.minWeight ?? 0) - (b.minWeight ?? 0));

      setPrices(combined);

      const currentPriceOption = combined.find(
        (option) =>
          currentPrice != null &&
          Math.abs(Number(option?.finalPrice ?? 0) - Number(currentPrice)) < 0.005
      );

      setSelectedPriceId(
        currentPriceOption?.id ??
        (pricingMode === "buy"
          ? hasCustomerGroup
            ? "group"
            : applicableTier?.id ?? "base"
          : groupName
            ? "group"
            : "base")
      );
    } catch (error) {
      console.error("Failed to build prices:", error);
      if (!isActiveRef.current) return;
      setPrices([]);
      setSelectedPriceId("");
    } finally {
      if (!isActiveRef.current) return;
      setIsLoading(false);
    }
  }, [
    open,
    productId,
    transaction,
    categoryId,
    products,
    pricingMode,
    currentWeight,
    currentPrice,
  ]);

  useEffect(() => {
    isActiveRef.current = open;
    if (!open) {
      lastFetchKeyRef.current = "";
      setSelectedPriceId("");
      setPrices([]);
      setData(null);
      setIsLoading(false);
      return;
    }
    const fetchKey = [
      transaction?.id ?? "",
      productId ?? "",
      categoryId ?? "",
      pricingMode,
    ].join("|");
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    setSelectedPriceId("");
    setPrices([]);
    setData(null);
    fetchCategoriesAndCompose();

    return () => {
      isActiveRef.current = false;
    };
  }, [open, fetchCategoriesAndCompose]);

  const handleChange = (event) => {
    setSelectedPriceId(String(event.target.value));
  };

  const handleConfirm = () => {
    const selectedPrice = prices.find(
      (p) => String(p.id) === String(selectedPriceId)
    );
    if (selectedPrice) {
      onSelect(selectedPrice);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        เลือกราคาสินค้า
        {productName && (
          <Typography color="primary" fontWeight={700} mt={0.5}>
            สินค้าปัจจุบัน: {productName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress />
          </Box>
        ) : prices.length === 0 ? (
          <Typography>ไม่มีราคาสินค้าให้เลือก</Typography>
        ) : (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              px={2}
              py={1}
              sx={{ color: "text.secondary" }}
            >
              <Typography>
                {pricingMode === "buy" ? "ตัวเลือกราคา" : "น้ำหนักขั้นต่ำ (กก.)"}
              </Typography>
              <Typography>ราคา (บาท/กก.)</Typography>
            </Box>

            <RadioGroup value={selectedPriceId ?? ""} onChange={handleChange}>
              {prices
                .filter((price) => price && price.id != null)
                .map((price) => (
                  <FormControlLabel
                    key={price.id}
                    value={String(price.id)}
                    control={<Radio />}
                    disableTypography
                    label={
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ flex: 1 }}
                      >
                        <Box>
                          <Typography>
                            {price.kind === "category"
                              ? `หมวด ${data?.name ?? ""} (ขั้นต่ำ ${price.minWeight ?? 0} กก.)`
                              : price.label}
                          </Typography>
                          {price.detail && (
                            <Typography variant="caption" color="text.secondary">
                              {price.detail}
                            </Typography>
                          )}
                        </Box>
                        <Typography>
                          {formatNumber(price?.finalPrice ?? 0)}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
            </RadioGroup>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ยกเลิก</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedPriceId}
        >
          ตกลง
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PriceSelectionDialog;
